import { FC, useCallback, useMemo } from 'react';
import { BaseEditor, createEditor, Descendant } from 'slate';
import { Slate, Editable, withReact, ReactEditor, RenderLeafProps } from 'slate-react';
import { RenderElementProps } from 'slate-react/dist/components/editable';

import styles from './editorViewer.module.scss';

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

type BlockFormatType =
  | 'title'
  | 'sub-title'
  | 'bulleted-list'
  | 'numbered-list'
  | 'list-item'
  | 'paragraph'
  | 'color-label'
  /**
   * NOTE: sprint/4-1 Text Size 재정의됨
   */
  | 't1'
  | 't2'
  | 't3'
  | 'image';

type CustomElement = {
  type: BlockFormatType;
  children: CustomElement[] | CustomText[];
  label?: string;
  size?: 'normal' | 'large';
  url?: string;
};

type CustomText = {
  text: string;
  bold?: boolean;
  highlight?: boolean;
  underline?: boolean;
  link?: string;
  label?: string;
};

type Props = {
  value: Descendant[];
  placeholder?: string;
};

const EditorViewer: FC<Props> = ({ value, placeholder }) => {
  const withColorLabel = (editor: ReactEditor) => {
    const { isInline, isVoid } = editor;

    editor.isInline = (element: CustomElement) => {
      return element.type === 'color-label' ? true : isInline(element);
    };

    editor.isVoid = (element: CustomElement) => {
      return element.type === 'color-label' ? true : isVoid(element);
    };

    return editor;
  };

  const editor = useMemo(() => withColorLabel(withReact(createEditor())), []);
  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);

  return (
    <Slate editor={editor} value={value} onChange={() => ({})}>
      <Editable
        renderPlaceholder={({ children, attributes }) => (
          <span {...attributes} className={styles['placeholder']}>
            {children}
          </span>
        )}
        placeholder={placeholder}
        className={styles['editor']}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        readOnly
      />
    </Slate>
  );
};

const Element = ({ attributes, children, element }: RenderElementProps) => {
  switch (element.type) {
    /************ deprecated ************/
    case 'title':
      return <h1 {...attributes}>{children}</h1>;
    case 'sub-title':
      return <h2 {...attributes}>{children}</h2>;
    /************************************/
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>;
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>;
    case 'list-item':
      return (
        <li {...attributes}>
          <div>{children}</div>
        </li>
      );
    case 'color-label':
      return element.size === 'large' ? (
        <h1 {...attributes}>
          {element.label}
          {children}
        </h1>
      ) : (
        <span className={styles['color-label']} {...attributes}>
          {element.label}
          {children}
        </span>
      );
    case 't1':
      return (
        <h1 className={styles.t1} {...attributes}>
          {children}
        </h1>
      );
    case 't2':
      return (
        <h2 className={styles.t2} {...attributes}>
          {children}
        </h2>
      );
    case 't3':
      return (
        <p className={styles.t3} {...attributes}>
          {children}
        </p>
      );
    case 'image':
      return (
        <div className={styles.image} {...attributes}>
          <img src={element?.url} alt={element.label} />
        </div>
      );
    default:
      return <p {...attributes}>{children}</p>;
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if (leaf.bold) {
    children = <b>{children}</b>;
  }

  if (leaf.highlight) {
    children = <strong>{children}</strong>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  if (leaf.link) {
    children = <a onClick={() => window.open(leaf.link, '_blank')}>{children}</a>;
  }

  return <span {...attributes}>{children}</span>;
};

export default EditorViewer;
